import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { RootState } from "src/redux/store";
import {
  listWcClaims,
  setSearchParameters,
  setSelectedCountries,
} from "../searchResults/searchResultsSlice";
import { WC_CLAIM_COLUMN_DEFINITIONS } from "../table/tableUtils";
import { CSV_CUSTOM_HEADERS, SEARCH_PARAMETERS } from "../searchResults/constants";
import ClaimsSearchBar from "../claimsSearchBar/ClaimsSearchBar";
import { MetricsPublisher } from "src/metrics";
import ExportToCSVButton from "../searchResults/ExportToCSVButton";
import SearchResults from "../searchResults/SearchResults";
import { ParsedClaimSummary } from "../searchResults/constants";
import { Flashbar, FlashbarProps, TextContent } from "@amzn/awsui-components-console";
import SimLinkGenerator from "../simLinkGenerator/SimLinkGenerator";
import { SIM_TYPES } from "../simLinkGenerator/types";
import { GENERAL_FORM_CONSTANTS } from "src/forms/constants";
import { WC_LINE_OF_BUSINESS_CONFIG, CountryCode } from "./constants";
import { CountryFilter } from "./CountryFilter";
import "src/styles/CountryFilter.scss";
import {
  getUserAccessibleCountries,
  shouldShowCountryFilter,
  getDefaultSelectedCountries,
} from "./countryUtils";

//Declare metricsPublishers, they will be initialized on first render
let metricsPublisher: MetricsPublisher;
let metricsPublisherVisitedBySite: MetricsPublisher;
let metricsPublisherSearchBarBySite: MetricsPublisher;
let metricsPublisherSearchByEmployeeId: MetricsPublisher;
let metricsPublisherSearchByAlias: MetricsPublisher;
let metricsPublisherSearchBySite: MetricsPublisher;

interface SearchParameters {
  searchBy: string;
  searchTerm: string;
  startDate?: number;
  endDate?: number;
}

export interface WorkersCompensationClaimsHistoryProps {
  /** Whether to show only claims submitted by the current user */
  showUserClaimsOnly?: boolean;
  /** Custom test ID for the component */
  testId?: string;
}

const CLAIMS_LIMIT = 100;

/**
 * Workers Compensation specific implementation of claims history search
 * Uses the generic SearchResults component with WC-specific configuration
 * @param props WorkersCompensationClaimsHistoryProps
 * @returns WorkersCompensationClaimsHistory component
 */
function WorkersCompensationClaimsHistory({
  showUserClaimsOnly = false,
  testId = "wc-claims-search-results",
}: WorkersCompensationClaimsHistoryProps) {
  //Redux Auth state
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  //Redux SearchResults state
  const {
    resultClaims,
    gettingClaims,
    isMoreResults,
    searchBy,
    searchTerm,
    startDate,
    endDate,
    listWcClaimsError,
    listWcClaimsErrorList,
    selectedCountries,
  } = useAppSelector((state: RootState) => state.searchResults);
  const { user } = useAppSelector((state: RootState) => state.auth);

  const [urlSearchParams] = useSearchParams();
  const [flashbarItems, setFlashBarItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [searchBarError, setSearchBarError] = useState<string | React.ReactNode>("");
  const [accessibleCountries, setAccessibleCountries] = useState<CountryCode[]>([]);
  const [showCountryFilter, setShowCountryFilter] = useState(false);
  const [countriesInitialized, setCountriesInitialized] = useState(false);

  // Initialize country filter based on user roles
  useEffect(() => {
    if (user.roles) {
      const countries = getUserAccessibleCountries(user.roles);
      setAccessibleCountries(countries);
      setShowCountryFilter(shouldShowCountryFilter(countries));

      // Set default selection if not already set
      if (selectedCountries.length === 0) {
        const defaultCountries = getDefaultSelectedCountries(countries);
        dispatch(setSelectedCountries(defaultCountries));
      }
      setCountriesInitialized(true);
    }
  }, [user.roles]);

  // Handle country selection changes
  const handleCountryChange = useCallback(
    (newCountries: CountryCode[]) => {
      dispatch(setSelectedCountries(newCountries));
    },
    [dispatch]
  );

  const triggerSearch = (searchParameters: SearchParameters) => {
    const { searchBy, searchTerm, startDate, endDate } = searchParameters;

    // Determine which countries to send based on filter visibility.
    // When the filter is visible, use the user's explicit selection from Redux.
    // When hidden (single country), auto-filter to the user's accessible country.
    const countriesToSend = showCountryFilter ? selectedCountries : accessibleCountries;

    // If showing user claims only, always search by current user
    if (showUserClaimsOnly && user.id) {
      dispatch(
        listWcClaims({
          claimReporterAlias: user.id,
          limit: CLAIMS_LIMIT,
          countries: countriesToSend,
        })
      );
      return;
    }

    // Regular search logic
    if (searchBy === SEARCH_PARAMETERS.EMPLOYEE_ID && searchTerm) {
      dispatch(
        listWcClaims({
          associateEmployeeID: searchTerm,
          countries: countriesToSend,
        })
      );
      return;
    }
    if (searchBy === SEARCH_PARAMETERS.ALIAS && searchTerm) {
      dispatch(
        listWcClaims({
          associateAlias: searchTerm,
          countries: countriesToSend,
        })
      );
      return;
    }

    if (searchBy === SEARCH_PARAMETERS.SITE && searchTerm && startDate && endDate) {
      dispatch(
        listWcClaims({
          incidentSite: searchTerm,
          // epoch timestamps are expected to be received in seconds by the backend. Date.parse returns them in milliseconds
          submittedStartDate: startDate! / 1000,
          submittedEndDate: endDate! / 1000,
          countries: countriesToSend,
        })
      );
      return;
    }
    if (searchBy === SEARCH_PARAMETERS.SITE && searchTerm) {
      dispatch(
        listWcClaims({
          incidentSite: searchTerm,
          countries: countriesToSend,
        })
      );
      return;
    }
  };

  // WC-specific search callback
  const handleSearch = (searchParams: SearchParameters) => {
    // Dispatch the setSearchParameters action with the processed search parameters
    dispatch(setSearchParameters(searchParams));

    // Navigate to the search route
    let startDateText = "";
    let endDateText = "";

    if (
      searchParams.searchBy === SEARCH_PARAMETERS.SITE &&
      searchParams.startDate &&
      searchParams.endDate
    ) {
      startDateText = `&${SEARCH_PARAMETERS.START_DATE}=${searchParams.startDate}`;
      endDateText = `&${SEARCH_PARAMETERS.END_DATE}=${searchParams.endDate}`;
    }

    const searchRoute =
      WC_LINE_OF_BUSINESS_CONFIG.routePath +
      `?${searchParams.searchBy}=${searchParams.searchTerm}${startDateText}${endDateText}`;

    navigate(searchRoute, { replace: true });
  };

  // WC-specific navigation callback for top navigation
  const handleNavigate = (route: string) => {
    window.open(route, "_blank", "noreferrer");
  };

  // WC-specific validation callback
  const validateSearchTerm = (searchTerm: string, searchBy: SEARCH_PARAMETERS): string | null => {
    const numberRegex = new RegExp(GENERAL_FORM_CONSTANTS.ONLY_NUMBERS_REGEX);
    if (searchBy === SEARCH_PARAMETERS.EMPLOYEE_ID && numberRegex.test(searchTerm)) {
      return "Employee ID must be only numbers.";
    }
    return null;
  };

  // WC-specific metrics callback
  const publishMetrics = (searchTerm: string, searchBy: SEARCH_PARAMETERS) => {
    if (searchBy === SEARCH_PARAMETERS.ALIAS) {
      metricsPublisherSearchByAlias.publishClaimsSearchByAlias(searchTerm);
    }
    if (searchBy === SEARCH_PARAMETERS.SITE) {
      metricsPublisherSearchBySite.publishClaimsSearchBySite(searchTerm);
    }
    if (searchBy === SEARCH_PARAMETERS.EMPLOYEE_ID) {
      metricsPublisherSearchByEmployeeId.publishClaimsSearchByEmployeeId(searchTerm);
    }
    metricsPublisher.publishClaimsSearchBarUsed();
    metricsPublisherSearchBarBySite.publishClaimsSearchBarUsedBySite(user.siteCode);
  };

  useEffect(() => {
    // Wait for countries to be initialized before triggering any search
    if (!countriesInitialized) {
      return;
    }

    // Initialize metrics publishers
    metricsPublisher = new MetricsPublisher("claimsSearch");
    metricsPublisherSearchBarBySite = new MetricsPublisher("claimsSearchBarUsedBySite");
    metricsPublisherSearchByEmployeeId = new MetricsPublisher("claimsSearchByEmployeeId");
    metricsPublisherSearchByAlias = new MetricsPublisher("claimsSearchByAlias");
    metricsPublisherSearchBySite = new MetricsPublisher("claimsSearchBySite");

    if (showUserClaimsOnly) {
      // For user claims, trigger search when user is loaded
      if (user.id) {
        triggerSearch({ searchBy: "", searchTerm: "" });

        // Initialize metrics for user claims
        metricsPublisher = new MetricsPublisher("mySubmittedClaims");
        metricsPublisherVisitedBySite = new MetricsPublisher("mySubmittedClaimsVisitedBySite");
        metricsPublisher.publishMySubmittedClaimsVisited();
        metricsPublisherVisitedBySite.publishMySubmittedClaimsVisitedBySite(user.siteCode);
      }
    } else {
      // Regular search results logic - convert URL params to SearchParameters format
      const employeeId = urlSearchParams.get(SEARCH_PARAMETERS.EMPLOYEE_ID);
      const alias = urlSearchParams.get(SEARCH_PARAMETERS.ALIAS)?.toLowerCase();
      const site = urlSearchParams.get(SEARCH_PARAMETERS.SITE);
      const startDateParam = urlSearchParams.get(SEARCH_PARAMETERS.START_DATE);
      const endDateParam = urlSearchParams.get(SEARCH_PARAMETERS.END_DATE);

      let searchParameters: SearchParameters;
      if (employeeId) {
        searchParameters = {
          searchBy: SEARCH_PARAMETERS.EMPLOYEE_ID,
          searchTerm: employeeId,
        };
      } else if (alias) {
        searchParameters = {
          searchBy: SEARCH_PARAMETERS.ALIAS,
          searchTerm: alias,
        };
      } else if (site) {
        searchParameters = {
          searchBy: SEARCH_PARAMETERS.SITE,
          searchTerm: site,
          startDate: startDateParam ? parseInt(startDateParam) : undefined,
          endDate: endDateParam ? parseInt(endDateParam) : undefined,
        };
      } else {
        searchParameters = { searchBy: "", searchTerm: "" };
      }

      triggerSearch(searchParameters);

      metricsPublisher = new MetricsPublisher("searchResultsVisited");
      metricsPublisherVisitedBySite = new MetricsPublisher("searchResultsVisitedBySite");
    }
  }, [showUserClaimsOnly, user.id, countriesInitialized]);

  // Set up error handling for search bar
  useEffect(() => {
    if (listWcClaimsError) {
      if (listWcClaimsErrorList?.length) {
        const parsedErrors: React.ReactNode = listWcClaimsErrorList.map((item: any) => {
          return (
            <li key={`fieldKey-${item.fieldName}`}>
              <div>
                {item.fieldName ? <b>{`${item.fieldName}: `} </b> : ""}
                {item.errorMessage}
              </div>
            </li>
          );
        });
        const errorMessage: React.ReactNode = (
          <TextContent>
            <div>
              There was a problem retrieving your search results. Please verify the following and
              try again:
            </div>
            <ul>{parsedErrors}</ul>
          </TextContent>
        );
        setSearchBarError(errorMessage);
      } else {
        setSearchBarError(
          <span>
            There was an error loading your search results. Please verify and try again. If the
            error persists please open a{" "}
            <SimLinkGenerator
              simType={SIM_TYPES.REPORT_A_BUG}
              target="_blank"
              ariaLabel="Pressing this link will open a new tab where you will be able to create a ticket for the team to investigate this issue."
            >
              support ticket
            </SimLinkGenerator>{" "}
            to get help with this issue.
          </span>
        );
      }
    } else {
      setSearchBarError("");
    }
  }, [listWcClaimsError, listWcClaimsErrorList]);

  // Set up error flashbar for user claims
  useEffect(() => {
    if (showUserClaimsOnly && listWcClaimsError) {
      setFlashBarItems([
        {
          type: "error",
          dismissible: true,
          dismissLabel: "Dismiss message",
          content: (
            <span>
              There was an error loading your submitted claims. Please reload the page. If the error
              persists please open a{" "}
              <SimLinkGenerator
                simType={SIM_TYPES.REPORT_A_BUG}
                target="_blank"
                ariaLabel="Pressing this link will open a new tab where you will be able to create a ticket for the team to investigate this issue."
              >
                support ticket
              </SimLinkGenerator>{" "}
              to get help with this issue.
            </span>
          ),
          id: "message_1",
          onDismiss: () => setFlashBarItems([]),
        },
      ]);
    } else {
      setFlashBarItems([]);
    }
  }, [showUserClaimsOnly, listWcClaimsError]);

  // Make sure user is loaded before logging metrics for regular search
  useEffect(() => {
    if (!showUserClaimsOnly && user.siteCode && metricsPublisher && countriesInitialized) {
      metricsPublisher.publishSearchResultsVisited();
      metricsPublisherVisitedBySite?.publishSearchResultsVisitedBySite(user.siteCode);
    }
  }, [showUserClaimsOnly, user, countriesInitialized]);

  // Trigger a search when any of the search parameters change (only for regular search)
  useEffect(() => {
    if (!showUserClaimsOnly) {
      const searchParameters: SearchParameters = {
        searchBy,
        searchTerm,
        startDate,
        endDate,
      };
      triggerSearch(searchParameters);
    }
  }, [showUserClaimsOnly, searchBy, searchTerm, startDate, endDate]);

  // Generate header content with WC-specific messaging
  const getHeaderContent = () => {
    if (showUserClaimsOnly) {
      const baseText = "My Submitted Claims";
      const limitText =
        resultClaims?.length >= CLAIMS_LIMIT ? ` (Most Recent ${CLAIMS_LIMIT})` : "";
      return `${baseText}${limitText}`;
    }

    const baseText = "Search Results";
    const moreResultsText =
      isMoreResults && searchBy === SEARCH_PARAMETERS.SITE
        ? " (More claims available, narrow down date range to load all claims)"
        : "";
    return `${baseText}${moreResultsText}`;
  };

  // Generate header actions with WC-specific export functionality
  const getHeaderActions = () => {
    const filePrefix = showUserClaimsOnly ? "my-submitted-claims" : "wc_claims_search_results";
    return (
      <ExportToCSVButton
        data={resultClaims as any[]}
        headers={CSV_CUSTOM_HEADERS}
        fileNamePrefix={filePrefix}
      />
    );
  };

  // Generate search bar component (country filter for all modes, search bar for regular search only)
  const getSearchBarComponent = () => {
    const countryFilterComponent = showCountryFilter ? (
      <div className="country-filter-container">
        <CountryFilter
          selectedCountries={selectedCountries as CountryCode[]}
          accessibleCountries={accessibleCountries}
          onChange={handleCountryChange}
          testId="wc-country-filter"
        />
      </div>
    ) : null;

    if (showUserClaimsOnly) {
      return countryFilterComponent;
    }
    return (
      <>
        {countryFilterComponent}
        <ClaimsSearchBar
          topNavigation={false}
          lineOfBusinessConfig={WC_LINE_OF_BUSINESS_CONFIG}
          onSearch={handleSearch}
          onNavigate={handleNavigate}
          validateSearchTerm={validateSearchTerm}
          publishMetrics={publishMetrics}
          errorMessage={searchBarError}
          onClearError={() => setSearchBarError("")}
        />
      </>
    );
  };

  // Generate flashbar for user claims errors
  const getFlashbarComponent = () => {
    if (showUserClaimsOnly && listWcClaimsError && flashbarItems.length > 0) {
      return <Flashbar data-testid="error-flashbar" items={flashbarItems} />;
    }
    return null;
  };

  return (
    <>
      {getFlashbarComponent()}
      <SearchResults<ParsedClaimSummary>
        items={resultClaims}
        columnDefinitions={WC_CLAIM_COLUMN_DEFINITIONS}
        loading={gettingClaims}
        loadingText="Loading Claims"
        resourceName="Claim"
        headerContent={getHeaderContent()}
        headerActions={getHeaderActions()}
        searchBarComponent={getSearchBarComponent()}
        filterPlaceholder="Filter claims"
        filterAriaLabel="Filter claims"
        defaultSorting={{ sortingField: "createdAtDate", isDescending: true }}
        pageSize={10}
        minItemsForControls={5}
        testId={testId}
      />
    </>
  );
}

export default WorkersCompensationClaimsHistory;
